import React from "react";
import type { Project } from "../../../api/useProjects";
import { useProjects } from "../../../api/useProjects";
import t from "../../../i18n";
import { css } from "@linaria/core";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Details from "@ui/Details";
import Section from "./Section";
import Checkbox from "@ui/Checkbox";
import { useSettings } from "../../../api/useSettings";
import { spacings } from "@ui/Theme";
import { colors } from "@ui/Theme";
import { AlertLine, CheckboxCircleLine, DownloadLine } from "@ui/IconV2/react";
import { fonts } from "@ui";
import download from "downloadjs";
import type { Output } from "../../../api/call";
import { generateExportFileName } from "diagnostics-lib/string";
import { styled } from "@linaria/react";
import Button from "@ui/Button";

export default function Result({
  patchResponse,
  project,
  className,
  allowSnapshotUpdatedOnConflict,
  setAllowSnapshotUpdatedOnConflict,
}: {
  patchResponse: Output<"PATCH", "/sources/snapshots">["body"];
  className: string;
  project: Project | null;
  allowSnapshotUpdatedOnConflict: boolean;
  setAllowSnapshotUpdatedOnConflict: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const settings = useSettings();

  const period = {
    min: patchResponse.startDate === null ? null : new Date(patchResponse.startDate),
    max: patchResponse.endDate === null ? null : new Date(patchResponse.endDate),
  };

  const projects = useProjects();
  const liveProjects = React.useMemo(
    () =>
      projects
        .filter(
          project => project.sourceId === patchResponse.sourceId && project.hasActivatedDataFlow,
        )
        .filter(({ name }) => name !== project?.name),
    [projects, patchResponse.sourceId, project?.name],
  );

  const liveProductionProjects = React.useMemo(
    () =>
      liveProjects
        .filter(project => project.draft === false)
        .filter(({ name }) => name !== project?.name),
    [project?.name, liveProjects],
  );

  const liveResearchProjects = React.useMemo(
    () =>
      liveProjects
        .filter(project => project.draft === true)
        .filter(({ name }) => name !== project?.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [project?.name, liveProjects],
  );

  const hasConflict = React.useMemo(
    () => patchResponse.conflictingSnapshotDiffs.length > 0,
    [patchResponse],
  );

  const hasAlreadyBeenProcessed = React.useMemo(
    () =>
      patchResponse.conflictingSnapshotDiffs.length === 0 &&
      patchResponse.newSnapshotsCount === 0 &&
      patchResponse.mergedSnapshotsCount === 0 &&
      patchResponse.ignoredSnapshotsCount > 0,
    [patchResponse],
  );

  return (
    <Section title={t("dataSource.import.analysis.section.title")} className={className}>
      <div className={container_xs}>
        <p>
          {t("dataSource.import.snapshotInfo", {
            sensorCount: patchResponse.sensorCount,
            startDate: period.min?.toLocaleDateString() ?? "<date-error>",
            endDate: period.max?.toLocaleDateString() ?? "<date-error>",
            snapshotCount:
              patchResponse.newSnapshotsCount +
              patchResponse.mergedSnapshotsCount +
              patchResponse.ignoredSnapshotsCount +
              patchResponse.conflictingSnapshotDiffs.length,
          })}
        </p>
        <ResultDetails
          newSnapshotCount={patchResponse.newSnapshotsCount}
          mergedSnapshotCount={patchResponse.mergedSnapshotsCount}
          ignoredSnapshotCount={patchResponse.ignoredSnapshotsCount}
          conflictingSnapshotsDiff={patchResponse.conflictingSnapshotDiffs}
        />
      </div>

      {settings.ALLOW_SNAPSHOT_UPDATES_ON_CONFLICT &&
      (hasConflict || allowSnapshotUpdatedOnConflict) ? (
        <div className={container_xs}>
          <p>{t("dataSource.import.conflict.confirmation_force_conflict.text")}</p>
          <Checkbox
            onChange={() => setAllowSnapshotUpdatedOnConflict(!allowSnapshotUpdatedOnConflict)}
            checked={allowSnapshotUpdatedOnConflict}
          >
            {t("dataSource.import.conflict.check.confirmation_force_conflict.text")}
          </Checkbox>
        </div>
      ) : null}

      {hasAlreadyBeenProcessed ? (
        <AdditionnalMessage
          className={PatchInfoBlock}
          status="INFO"
          text={t("dataSource.import.snapshotInfo.empty")}
        />
      ) : (
        <>
          {liveProjects.length > 0 && (
            <AdditionnalMessage
              className={PatchInfoBlock}
              status={
                project
                  ? liveProjects.find(project => project.id !== project?.id)
                    ? "WARNING"
                    : "INFO"
                  : "INFO"
              }
              text={t("dataSource.import.projectInfo", {
                sourceId: patchResponse.sourceId,
                projectCount: liveProjects.length,
                productionProjectNames: liveProductionProjects.map(project => project.name),
                researchProjectNames: liveResearchProjects.map(project => project.name),
                inProject: Boolean(project),
              })}
            />
          )}
          {liveResearchProjects.length > 0 &&
            patchResponse.conflictingSnapshotDiffs.length === 0 && (
              <StyledDetails
                className={PatchInfoBlock}
                summary={t(
                  "dataSource.import.projectInfo.details.summary",
                  liveResearchProjects.length,
                )}
                text={
                  <ul className={ResearchProjectList}>
                    {liveResearchProjects.map(project => (
                      <li key={project.id}>{project.name}</li>
                    ))}
                  </ul>
                }
              />
            )}
        </>
      )}
    </Section>
  );
}

function ResultDetails({
  conflictingSnapshotsDiff,
  newSnapshotCount,
  mergedSnapshotCount,
  ignoredSnapshotCount,
}: {
  newSnapshotCount: number;
  mergedSnapshotCount: number;
  ignoredSnapshotCount: number;
  conflictingSnapshotsDiff: Output<
    "PATCH",
    "/sources/snapshots"
  >["body"]["conflictingSnapshotDiffs"];
}) {
  const conflictSnapshotCount = conflictingSnapshotsDiff.length;

  const exportConflictsSnapshot = React.useCallback(() => {
    const jsonConflicts = JSON.stringify(conflictingSnapshotsDiff, null, 4);
    const blob = new Blob([jsonConflicts as unknown as Blob], {
      type: "application/json;charset=utf-8",
    });
    const fileName = generateExportFileName({
      filename: "upsert_snapshot_conflits",
      extension: "json",
    });
    download(blob, fileName, "application/json");
  }, [conflictingSnapshotsDiff]);

  return (
    <ul className={snapshotInfoList}>
      {newSnapshotCount ? (
        <li className={`${List} ${InfoItem}`}>
          <CheckboxCircleLine size="s" color={colors.carbon800} />
          {t("dataSource.import.snapshotInfo.details.text.newSnapshot", newSnapshotCount)}
        </li>
      ) : null}
      {mergedSnapshotCount ? (
        <li className={`${List} ${InfoItem}`}>
          <CheckboxCircleLine size="s" color={colors.carbon800} />
          {t("dataSource.import.snapshotInfo.details.text.mergedSnapshot", mergedSnapshotCount)}
        </li>
      ) : null}
      {ignoredSnapshotCount ? (
        <li className={`${List} ${InfoItem}`}>
          <CheckboxCircleLine size="s" color={colors.carbon800} />
          {t("dataSource.import.snapshotInfo.details.text.ignoredSnapshot", ignoredSnapshotCount)}
        </li>
      ) : null}
      {conflictSnapshotCount > 0 && (
        <li className={`${List} ${errorItem}`}>
          <AlertLine size="s" />
          <div>
            <p>
              {t(
                "dataSource.import.snapshotInfo.details.text.conflictSnapshot",
                conflictSnapshotCount,
              )}
            </p>
            <p>{t("dataSource.import.snapshotInfo.details.text.conflictSnapshot_info")}</p>
            <StyledButton
              variant="link"
              onClick={exportConflictsSnapshot}
              leftIcon={<DownloadLine size="s" />}
            >
              {t("dataSource.import.snapshot.conflict.export")}
            </StyledButton>
          </div>
        </li>
      )}
    </ul>
  );
}

const StyledButton = styled(Button)`
  font: ${fonts.body2};
`;

const snapshotInfoList = css`
  font: ${fonts.body2};

  list-style: none;

  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const List = css`
  display: flex;
  gap: 4px;

  & > svg {
    flex-shrink: 0;
  }

  & > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    & > p {
      font: ${fonts.body2};
    }
  }
`;

const InfoItem = css`
  align-items: center;
`;

const errorItem = css`
  align-items: flex-start;
  color: ${colors.phosphorus600};
`;

const StyledDetails = styled(Details)`
  summary ~ div {
    margin-top: ${spacings.xxs};
  }
`;

const PatchInfoBlock = css`
  & + & {
    margin-top: ${spacings.xs};
  }
`;

const container_xs = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const ResearchProjectList = css`
  list-style: disc;
  padding-left: 3em;
`;
